import {
  ChangeDetectorRef,
  Component,
  OnChanges,
  SimpleChanges,
  input,
  inject,
  viewChildren,
  output,
  signal,
  computed,
} from '@angular/core';
import { Tool } from '../models/tool.models';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FileUploadComponent } from './file-upload/file-upload.component';
import { InputMapping, isInputWithMapping, ToolInput } from '../models/toolchain.models';
import { isInputMappingWithValue } from '../../capability/job-page/job-timeline/toolchain-timeline.util';
import { ValueInputComponent } from './value-input/value-input.component';
import { TableInputComponent } from './table-input/table-input.component';
import { MatTooltip } from '@angular/material/tooltip';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { startWith, switchMap } from 'rxjs';
import { ButtonDirective } from '@innersource/common-ui-components';

@Component({
  selector: 'app-execution-form',
  templateUrl: './execution-form.component.html',
  styleUrls: ['./execution-form.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ValueInputComponent,
    FileUploadComponent,
    TableInputComponent,
    MatTooltip,
    ButtonDirective,
  ],
})
export class ExecutionFormComponent implements OnChanges {
  readonly tool = input<Tool>();

  readonly outputsToMap = input<InputMapping[]>([]);
  readonly submitting = input(false);

  protected readonly isInputWithMapping = isInputWithMapping;

  private readonly cdRef = inject(ChangeDetectorRef);

  toolInputs = signal<ToolInput[]>([]);
  form = computed<FormGroup>(() => {
    const group: Record<string, AbstractControl> = {};
    this.toolInputs().forEach(input => {
      let defaultValue = input.param.value;
      let disabled = false;

      // specific handling for input with mapping
      // assign default value to valueToMap
      // disable form field if it should not be modified
      if (isInputWithMapping(input)) {
        if (Array.isArray(input.valueToMap)) {
          if (!input.isSelectable) {
            defaultValue = input.valueToMap;
            disabled = true;
          }
        } else {
          defaultValue = input.valueToMap;
          disabled = !input.isModifiable;
        }
      }
      group[input.name] = new FormControl(
        { value: defaultValue, disabled: disabled },
        input.isOptional ? [] : Validators.required
      );
    });
    return new FormGroup(group);
  });

  readonly formSubmitted = output<FormGroup>();

  readonly fileUploadComps = viewChildren(FileUploadComponent);

  /** Table inputs in the current form. */
  readonly tableInputComps = viewChildren(TableInputComponent);

  /** Whether any table input has an active inline edit. */
  readonly isTableEditing = computed(() => this.tableInputComps().some(table => table.isEditing()));

  formUpdateTrigger = toSignal(
    toObservable(this.form).pipe(switchMap(form => form.valueChanges.pipe(startWith(form.value))))
  );

  invalidInputs = computed(() => {
    this.formUpdateTrigger();
    const invalid: string[] = [];
    const controls = this.form().controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        const displayName = this.toolInputs().filter(input => input.name == name)[0].displayName;
        invalid.push(displayName + '  is missing');
      }
    }
    return invalid.join(', ');
  });

  ngOnChanges(changes: SimpleChanges) {
    if (changes['tool'] && this.tool()) {
      this.toolInputs.set(this.setToolInput());
    }
  }

  setToolInput() {
    let toolInputs: ToolInput[] = [];
    const tool = this.tool();
    if (tool) {
      if (this.outputsToMap().length > 0) {
        for (const toolInput of tool.inputs) {
          const outputToMap = this.outputsToMap().filter(o => o.inputName == toolInput.name);
          if (outputToMap.length === 1 && isInputMappingWithValue(outputToMap[0])) {
            const hidden: boolean = outputToMap[0].isHidden ? outputToMap[0].isHidden : false;
            toolInputs.push({
              ...toolInput,
              isHidden: hidden,
              valueToMap: outputToMap[0].outputValue,
              isSelectable: outputToMap[0].isSelectable,
              isModifiable: outputToMap[0].isModifiable,
              modifications: outputToMap[0].modifications,
            } as ToolInput);
          } else {
            toolInputs.push(toolInput);
          }
        }
      } else {
        toolInputs = tool.inputs;
      }
    }
    return toolInputs;
  }

  resetForm() {
    this.form().reset();
    for (const comp of this.fileUploadComps()) {
      comp.resetInput();
    }
  }

  onSubmit() {
    if (this.submitting() || this.form().invalid || this.isTableEditing()) return;
    this.formSubmitted.emit(this.form());
  }

  protected onFileSelect(inputName: string, files: File[]) {
    const file_group: FormControl[] = [];
    for (const file of files) {
      file_group.push(new FormControl(file));
    }
    this.form().controls[inputName].setValue(new FormGroup(file_group));

    // need to run CD since file load runs outside of zone
    this.cdRef.markForCheck();
  }
}
